import { analytics } from "@/lib/analytics";
import {
  useConfirmExternalCheckout,
  useExternalCheckout
} from "@/queries/cart";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { isFetchError } from "@repo/api-sdk";
import { memo, useEffect, useMemo, useState } from "react";
import { ExternalCheckoutDialogView } from "./ExternalCheckoutDialogView";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface ExternalCheckoutDialogProps {
  /** The seller cart being handed off to the supplier's external checkout. */
  cart: StoreSellerCart;
  /** Whether the interstitial is open. */
  open: boolean;
  /** Open-state change callback. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Container for the external-checkout interstitial. Fetches the checkout URL
 * when the dialog opens, maps the mutation state onto
 * {@link ExternalCheckoutDialogView}, and on confirm fires the client analytics
 * event and redirects to the supplier's store.
 */
export const ExternalCheckoutDialog = memo(function ExternalCheckoutDialog({
  cart,
  open,
  onOpenChange
}: ExternalCheckoutDialogProps) {
  const { mutate, data, error, isPending, isError, reset } =
    useExternalCheckout();
  const { mutateAsync: confirmHandoff } = useConfirmExternalCheckout();
  const [acknowledged, setAcknowledged] = useState(false);

  // Fetch the checkout URL when the dialog opens; reset when it closes so a
  // reopen starts clean.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      mutate(cart.cart_id);
    } else {
      reset();
    }
    // `mutate`/`reset` are stable react-query callbacks; we intentionally
    // re-run only when the dialog opens or the target cart changes.
  }, [open, cart.cart_id]);

  const unmapped = useMemo(() => data?.unmapped ?? [], [data?.unmapped]);

  const errorMessage = isError
    ? isFetchError(error) && error.status === 422
      ? "None of these items can be ordered directly with this supplier right now."
      : "We couldn't start checkout with this supplier. Please try again."
    : null;

  const handleConfirm = () => {
    if (!data?.url) return;
    // Open the supplier's store in a new tab so salonstocker stays open in this
    // one and the customer can carry on with their other supplier carts. This
    // runs synchronously inside the click gesture so the browser doesn't treat
    // it as a blocked popup.
    const supplierTab = window.open(data.url, "_blank");
    if (supplierTab) {
      // Sever the opener link so the supplier's store can't script this tab
      // (tabnabbing protection).
      supplierTab.opener = null;
    } else {
      // A blocker returned no handle - fall back to replacing this tab so the
      // customer still reaches checkout.
      window.location.href = data.url;
      return;
    }
    analytics.externalCheckoutConfirmed({
      seller_id: cart.seller.id,
      platform: data.platform,
      item_count: Math.max(cart.items.length - unmapped.length, 0)
    });
    // Mark the cart as handed off now that the customer has confirmed.
    // Best-effort and fire-and-forget: closing the dialog shouldn't wait on it,
    // and the customer is already in the supplier's tab.
    confirmHandoff(cart.cart_id).catch(() => {
      // Swallow - the query invalidation still refreshes the cart on success.
    });
    // The new tab is open; close the interstitial.
    onOpenChange(false);
  };

  return (
    <ExternalCheckoutDialogView
      open={open}
      onOpenChange={onOpenChange}
      sellerName={cart.seller.name}
      platform={data?.platform}
      isLoading={isPending}
      errorMessage={errorMessage}
      ready={!!data?.url}
      unmapped={unmapped}
      acknowledged={acknowledged}
      onAcknowledgedChange={setAcknowledged}
      onConfirm={handleConfirm}
    />
  );
});
